import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface AtendimentoIframe {
  id: string
  providerId: string
  nome: string
}

export interface SistemaExternoAtivo extends AtendimentoIframe {
  cidadaoId?: ID
}

interface SistemasExternosAtendimentoContextModel {
  sistemaExternoAtivo: SistemaExternoAtivo | null
  registrarSistemaExternoAtivo: (iframe: AtendimentoIframe, cidadaoId?: ID) => void
  resetSistemaExterno: () => void
}

interface SistemasExternosAtendimentoState {
  scopeKey: string
  sistemaExternoAtivo: SistemaExternoAtivo | null
}

const SistemasExternosAtendimentoContext = createContext<SistemasExternosAtendimentoContextModel | undefined>(undefined)

interface SistemasExternosAtendimentoProviderProps {
  children: React.ReactNode
  scopeKey: string
}

export function isMesmoSistemaExterno(
  left: Pick<AtendimentoIframe, 'id' | 'providerId'> | null | undefined,
  right: Pick<AtendimentoIframe, 'id' | 'providerId'> | null | undefined
) {
  return !!left && !!right && left.id === right.id && left.providerId === right.providerId
}

export function SistemasExternosAtendimentoProvider(props: Readonly<SistemasExternosAtendimentoProviderProps>) {
  const { children, scopeKey } = props
  const [state, setState] = useState<SistemasExternosAtendimentoState>({
    scopeKey,
    sistemaExternoAtivo: null,
  })

  const sistemaExternoAtivo = state.scopeKey === scopeKey ? state.sistemaExternoAtivo : null

  const resetSistemaExterno = useCallback(() => {
    setState({ scopeKey, sistemaExternoAtivo: null })
  }, [scopeKey])

  const registrarSistemaExternoAtivo = useCallback(
    (iframe: AtendimentoIframe, cidadaoId?: ID) => {
      setState((current) => {
        const sistemaExternoAtivo = { ...iframe, cidadaoId }
        if (
          current.scopeKey === scopeKey &&
          isMesmoSistemaExterno(current.sistemaExternoAtivo, sistemaExternoAtivo) &&
          current.sistemaExternoAtivo?.cidadaoId === cidadaoId
        ) {
          return current
        }

        return { scopeKey, sistemaExternoAtivo }
      })
    },
    [scopeKey]
  )

  const value = useMemo<SistemasExternosAtendimentoContextModel>(
    () => ({
      sistemaExternoAtivo,
      registrarSistemaExternoAtivo,
      resetSistemaExterno,
    }),
    [registrarSistemaExternoAtivo, resetSistemaExterno, sistemaExternoAtivo]
  )

  return (
    <SistemasExternosAtendimentoContext.Provider value={value}>{children}</SistemasExternosAtendimentoContext.Provider>
  )
}

export function useSistemasExternosAtendimento() {
  const context = useContext(SistemasExternosAtendimentoContext)
  if (!context) {
    throw new Error('useSistemasExternosAtendimento deve ser usado dentro de SistemasExternosAtendimentoProvider')
  }
  return context
}
